/**
 * Pure, framework-free matcher that decides whether an assistant chat message
 * is an EXPLICIT farewell that should auto-close the chat panel.
 *
 * Lives as plain JS (not .tsx) so it can be imported and behaviorally tested
 * by node:test AND by the customer chat sanity harness (sanity-chat-tomer-30),
 * while ChatModePanel.tsx imports the exact same logic the harness checks.
 *
 * REGRESSION CONTEXT (June 2026): a live chat "closed all of a sudden" because
 * this matcher treated routine courtesy phrases said mid-conversation —
 * "שמחתי לעזור" (glad I helped), "תודה שפנית" (thanks for reaching out), a bare
 * "יום טוב" (good day), "thanks for chatting" — as session-ending farewells.
 * Only an UNAMBIGUOUS sign-off may auto-close. Every genuine end-of-flow
 * message the bot emits (booking confirmed, callback registered, etc.) already
 * contains one of the explicit farewells below.
 *
 * @param {string} text raw assistant message text
 * @returns {boolean} true only when the chat should auto-close
 */
export function isTerminalGoodbye(text) {
  const normalized = String(text || '').replace(/\s+/g, ' ').trim().toLowerCase()
  if (!normalized) {
    return false
  }

  // Never close while the assistant is still inviting the user to continue.
  const asksToContinue =
    /\?|עוד משהו|אפשר לעזור|אשמח לעזור|רוצה שא|רוצה עוד|תרצה|תרצי|would you like|anything else|can i help|need more/i.test(
      normalized
    )
  if (asksToContinue) {
    return false
  }

  // Only an EXPLICIT, unambiguous farewell auto-closes the chat. Courtesy
  // phrases ("שמחתי לעזור", "תודה שפנית", bare "יום טוב") are routinely said
  // mid-conversation and must NOT end the session.
  return /להתראות|ביי|המשך יום|להשתמע|goodbye|\bbye\b|have a (?:great|nice|good|wonderful) day|take care\b|au revoir|до свидания|مع السلامة/i.test(
    normalized
  )
}
